import { TableColumnHeader } from "@/components/shared/table/Table";
import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";
import { convertBpToDecimal, formatDateLocale, formatNumberLocale, getProvinceNameByCode } from "@/utils/helpers";
import { IntlShape } from "react-intl";
import { TFunction } from "i18next";
import { Tariff } from "@/graphql/__generated__/graphql";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TrashIcon } from "lucide-react";

/**
 * TooltipCell is a component that wraps the children in a tooltip.
 * @param children {children: React.ReactNode}
 * @returns {React.ReactNode}
 */
const TooltipCell = ({ children, t }: { children: React.ReactNode, t: TFunction }) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent>
        <p>{t("tables:tariff.tooltipnote")}</p>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * When pinning multiple columns, if there is a gap between pinnted columns.
 * Adjust the size, minSize, maxSize values.
 */

export const getColumnsTariff = (
  intl: IntlShape,
  t: TFunction,
  handleRemoveTariff: (_tariff: Tariff) => void,
  isConfigAdmin: boolean,
): ColumnDef<Tariff>[] => [
  {
    id: "province",
    accessorKey: "province",
    size: 120,
    minSize: 120,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.province")}
        column={column}
      />
    ),
    cell: ({ row }) => {
      const provinceCode = row.getValue("province") as string;
      return <div>{getProvinceNameByCode(provinceCode, true)}</div>;
    }
  },
  {
    id: "activeFrom",
    accessorKey: "activeFrom",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.activationdate")}
        column={column}
      />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("activeFrom"));
      return <div>{formatDateLocale(intl, date)}</div>;
    }
  },
  {
    id: "workdaysPerYear",
    accessorKey: "workdaysPerYear",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.workdaysperyear")}
        column={column}
      />
    )
  },
  {
    id: "startNight",
    accessorKey: "startNight",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.startnight")}
        column={column}
      />
    ),
  },
  {
    id: "endNight",
    accessorKey: "endNight",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.endnight")}
        column={column}
      />
    ),
  },
  {
    id: "bonusNightRelative",
    accessorKey: "bonusNightRelative",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.bonusnightrelative")}
        column={column}
        align="end"
      />
    ),
    cell: ({ row }) => {
      const relValue = parseFloat(row.getValue("bonusNightRelative"));
      const absValue = row.original.bonusNightAbsolute;
      return <TooltipCell t={t}>
        <div className="flex flex-row gap-2 justify-end font-medium">
          {convertBpToDecimal(relValue)}%
          {absValue && absValue !== 0 ? ` (${formatNumberLocale(intl, absValue, "currency")})` : ""}
        </div>
      </TooltipCell>;
    }
  },
  {
    id: "bonusSundayRelative",
    accessorKey: "bonusSundayRelative",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.bonussundayrelative")}
        column={column}
        align="end"
      />
    ),
    cell: ({ row }) => {
      const relValue = parseFloat(row.getValue("bonusSundayRelative"));
      const absValue = row.original.bonusSundayAbsolute;
      return <TooltipCell t={t}>
        <div className="flex flex-row gap-2 justify-end font-medium">
          {convertBpToDecimal(relValue)}%
          {absValue && absValue !== 0 ? ` (${formatNumberLocale(intl, absValue, "currency")})` : ""}
        </div>
      </TooltipCell>;
    }
  },
  {
    id: "bonusSundayNightRelative",
    accessorKey: "bonusSundayNightRelative",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.bonussundaynightrelative")}
        column={column}
        align="end"
      />
    ),
    cell: ({ row }) => {
      const relValue = parseFloat(row.getValue("bonusSundayNightRelative"));
      const absValue = row.original.bonusSundayNightAbsolute;
      return <TooltipCell t={t}>
        <div className="flex flex-row gap-2 justify-end font-medium">
          {convertBpToDecimal(relValue)}%
          {absValue && absValue !== 0 ? ` (${formatNumberLocale(intl, absValue, "currency")})` : ""}
        </div>
      </TooltipCell>;
    }
  },
  {
    id: "bonusHolidayRelative",
    accessorKey: "bonusHolidayRelative",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.bonusholidayrelative")}
        column={column}
        align="end"
      />
    ),
    cell: ({ row }) => {
      const relValue = parseFloat(row.getValue("bonusHolidayRelative"));
      const absValue = row.original.bonusHolidayAbsolute;
      return <TooltipCell t={t}>
        <div className="flex flex-row gap-2 justify-end font-medium">
          {convertBpToDecimal(relValue)}%
          {absValue && absValue !== 0 ? ` (${formatNumberLocale(intl, absValue, "currency")})` : ""}
        </div>
      </TooltipCell>;
    }
  },
  {
    id: "bonusHolidayNightRelative",
    accessorKey: "bonusHolidayNightRelative",
    size: 120,
    minSize: 100,
    maxSize: 200,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.bonusholidaynightrelative")}
        column={column}
        align="end"
      />
    ),
    cell: ({ row }) => {
      const relValue = parseFloat(row.getValue("bonusHolidayNightRelative"));
      const absValue = row.original.bonusHolidayNightAbsolute;
      return <TooltipCell t={t}>
        <div className="flex flex-row gap-2 justify-end font-medium">
          {convertBpToDecimal(relValue)}%
          {absValue && absValue !== 0 ? ` (${formatNumberLocale(intl, absValue, "currency")})` : ""}
        </div>
      </TooltipCell>;
    }
  },
  {
    id: "basisForBonusCalculation",
    accessorKey: "basisForBonusCalculation",
    size: 150,
    minSize: 120,
    maxSize: 300,
    header: ({ column }) => (
      <TableColumnHeader
        text={t("tables:tariff.columns.basisforbonuscalculation")}
        column={column}
      />
    ),
    cell: ({ row }) => {
      const basisForBonusCalculation = row.getValue("basisForBonusCalculation") as string;
      return <div>{t(`common:basisforbonuscalculation.${(basisForBonusCalculation).toLowerCase()}`)}</div>;
    }
  },
  ...(isConfigAdmin
    ? [
      {
        accessorKey: "actions",
        size: 40,
        header: () => <div className="flex justify-end"></div>,
        cell: ({ row }) => (
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveTariff(row.original)}
            >
              <TrashIcon className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ),
      },
    ]
    : []),
];
